import { ChangeEvent, useEffect } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton } from '@mui/material';

import { filesAPIService } from '~/api/files';
import { listOfLinksMicrositeAPIService } from '~/api/microsites/links';
import DeleteIcon from '~/assets/icons/svgs/trash-bin.svg?react';
import UrlTextField from '~/components/atoms/UrlTextField';
import Accordion from '~/components/ui/Accordion/Accordion';
import ImageUploader from '~/components/ui/ImageUploader/ImageUploader';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { useAppDispatch } from '~/hooks/useRedux';
import { listOfLinksSlice, updateLinksThunk } from '~/store/reducers/previewScreens/list-of-links';
import { isMaxLength } from '~/utils/common';
import { logger } from '~/utils/logger';

import { useStyles } from './ItemImage.styled';

const { actions } = listOfLinksSlice;

export const ImageItem = ({ index, item, linkList, setClosedAccordions, micrositeId, dragTrigger, stopTrigger }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const styles = useStyles();

  const linkListRequest = linkList.map((item) => ({
    photo_id: item.imageStorageId,
    url: item.url,
    text: item.linkText,
  }));

  const addNewImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files[0];
    const previewLink = window.URL.createObjectURL(file);

    try {
      const storageResponse = await filesAPIService.uploadImage(file);

      linkListRequest[index].photo_id = storageResponse.data;

      dispatch(
        updateLinksThunk({
          id: item.unique_id,
          body: { photo_id: storageResponse.data },
          dispatchHandler: () => {
            dispatch(
              actions.setContentLinkListValues({
                field: 'previewLink',
                value: previewLink,
                index,
              }),
            );

            dispatch(
              actions.setContentLinkListValues({
                field: 'imageStorageId',
                value: storageResponse.data,
                index,
              }),
            );
          },
        }),
      );
    } catch (e) {
      logger.error(e);
    }
  };

  const removeIcon = () => {
    linkListRequest[index].photo_id = '';

    dispatch(
      updateLinksThunk({
        id: item.unique_id,
        body: { photo_id: null },
        dispatchHandler: () => {
          dispatch(actions.setContentLinkListValues({ field: 'previewLink', value: '', index }));
        },
      }),
    );
  };

  const handleRemoveItem = async (event) => {
    event.stopPropagation();

    await listOfLinksMicrositeAPIService.deleteLink(item.unique_id);
    dispatch(actions.setContentLinkList(linkList.filter((item, ind) => ind !== index)));
    setClosedAccordions((prevAccordions) => prevAccordions.filter((item, i) => i !== index));
  };

  const handleChangeItemValues = (e, field) => {
    dispatch(actions.setContentLinkListValues({ field, value: e.target.value, index }));
  };

  const handleChangeItemValuesOnBlur = (requestField) => {
    dispatch(
      updateLinksThunk({
        id: item.unique_id,
        body: { [requestField]: linkListRequest[index][requestField] },
      }),
    );
  };

  useEffect(() => {
    if (micrositeId && !stopTrigger) {
      dispatch(
        updateLinksThunk({
          id: item.unique_id,
          body: { position: index },
        }),
      );
    }
  }, [dragTrigger]);

  return (
    <Draggable key={item?.id} draggableId={item?.id} index={index}>
      {(provided) => (
        <Box ref={provided.innerRef} {...provided.draggableProps} sx={styles.wrapper}>
          <Accordion
            defaultExpanded
            variant="draggableItem"
            summary={
              <Box sx={styles.header}>
                <Text variant="t16Regular" sx={{ whiteSpace: 'nowrap' }}>
                  {`${t('titles:link')} ${index + 1}`}
                </Text>
                <Box sx={styles.icons}>
                  <IconButton disableRipple {...provided.dragHandleProps}>
                    <MenuIcon />
                  </IconButton>
                  <IconButton disableRipple onClick={handleRemoveItem}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            }
            details={
              <>
                <ImageUploader
                  image={item?.previewLink}
                  onDeleteImage={removeIcon}
                  onUploadImage={(e) => addNewImage(e)}
                />
                <TextInput
                  labelSx={{ marginTop: '12px' }}
                  fullWidth
                  label={t('labels:linkText')}
                  placeholder={t('common:writeLinkText')}
                  defaultValue={item?.linkText}
                  onChange={(e) => handleChangeItemValues(e, 'linkText')}
                  onBlur={() => handleChangeItemValuesOnBlur('text')}
                  inputProps={{ maxLength: 50 }}
                  helperText={(!item?.linkText && t('inputs:required')) || isMaxLength(item?.linkText, 50)}
                  error={!item?.linkText}
                />
                <UrlTextField
                  urlValue={item.url}
                  requireProtocol={false}
                  placeholder="https://www.example.com/"
                  label={t('labels:url')}
                  labelSx={{ marginTop: '12px' }}
                  onChange={(e) => {
                    handleChangeItemValues(e, 'url');
                  }}
                  onBlur={({ isValid }) => isValid && handleChangeItemValuesOnBlur('url')}
                />
              </>
            }
          />
        </Box>
      )}
    </Draggable>
  );
};
